import React, { Component } from "react";
import {
	convertUTCToLocalDateTime,
	isValidDate,
	getImgSrc,
} from "../../helpers/GlobalFunctions";
import CustomModal from "../../shared/CustomModal";
import { actions, errorMessages, loggerEventMessage, loggerEventOutcome, loggerEventName, loggerEventTypes } from "../../helpers/messages";
import {
  uploadPreOpPlanZip,
  reviewPreOpPlanDetails,
  confirmUploadPreOpPlanZip,
  uploadPreOpPlanShoulderZip,
  reviewPreOpPlanShoulderDetails,
  confirmUploadShoulderPreOpPlanZip,
} from "../../services/java/java-services";
import { logger } from '../../services/logger/logger-service';
import { eventSource } from '../../helpers/constants';

export default class PreOpPlanManualUpload extends Component {
	constructor(props) {
		super(props);
		this.inputOpenFileRef = React.createRef();

		this.state = {
			showModal: false,
			selectedFile: null,
			errorMessage: "",
			isChecked: false,
			fileName: "",
			disclaimerCheckBox: "",
			isUploading: false,
			fileDataUploadConfirmation: false,
			uploadSuccess: false,
			preopCaseData: "",
			duplciatePreopPlan: false,
			procedureType: "knee",
		};
	}

	/**
	 * @description function to reset the state values after modal close
	 * @memberof PreOpPlanManualUpload
	 */
	resetForm = () => {
		this.setState({
			errorMessage: "",
			selectedFile: null,
			fileName: "",
			isChecked: false,
			disclaimerCheckBox: "",
			isUploading: false,
			fileDataUploadConfirmation: false,
			uploadSuccess: false,
			preopCaseData: "",
			duplciatePreopPlan: false,
		});
	};

	/**
	* @description function to handle file selection from the input field
	* @memberof PreOpPlanManualUpload
	*/
	showOpenFileDlg = () => this.inputOpenFileRef.current.click();

	/**
	 * @description function to handle file upload modal on click on upload button
	 * @memberof PreOpPlanManualUpload
	 */
	openModal = () => {
		this.setState({
			showModal: true,
			errorMessage: "",
			selectedFile: null,
			fileName: "",
			isChecked: false,
			disclaimerCheckBox: "",
			isUploading: false,
			fileDataUploadConfirmation: false,
			uploadSuccess: false,
			preopCaseData: "",
			duplciatePreopPlan: false,
			procedureType: "knee",
		})
	};

  /**
     * @description function to handle modal close with logger
     * @memberof PreOpPlanManualUpload
     */
    closeModalWithLog = () => {
      let loggerObj = {
          "EventOutcome": loggerEventOutcome.success,
          "EventType": loggerEventTypes.manualUpload,
           "EventSource": eventSource.kpopPlans.value,
          "EventName": loggerEventName.preOpPlanManualUploadCancel,
          "Content": {
            "Data": loggerEventMessage.preOpUploadReviewCancel,
            "CaseId": this.state.preopCaseData?.caseNumber
          }
        }
      
        logger(loggerObj);
        this.closeModal()
    };

	/**
	* @description function to handle modal close
	* @memberof PreOpPlanManualUpload
	*/
	closeModal = () => {
		if (this.state.isUploading) {
			return;
		}
		this.resetForm();
		this.setState({ showModal: false });
	};

	/**
	 * @description function to handle file upload
	 * @memberof PreOpPlanManualUpload
	 */
	handleChange = (selectedFile) => {
		if (!selectedFile[0]) return;

		const file = selectedFile[0];
		const fileName = file.name;
		const fileSize = file.size;
		const maxFileSize = 5368709120; // 5GB

		if (fileName.split(".").pop().toLowerCase() !== "zip") {
			return this.setState({
				errorMessage: errorMessages.fileUpload.selectZip,
			});
		}

		if (fileSize > maxFileSize) {
			return this.setState({
				errorMessage: errorMessages.fileUpload.maxSizeAllowed,
			});
		}

		this.setState({ fileName, selectedFile: file, errorMessage: "" });
	};

	/**
	 * @description function to render the date format field 
	 * @memberof PreOpPlanManualUpload
	 */
	renderDate = (updatedDate, trimTime = false) => {
		if (!updatedDate || !isValidDate(updatedDate)) {
			return 'N/A';
		}
		let date = convertUTCToLocalDateTime(updatedDate);
		if (trimTime) {
			let dateArray = updatedDate.split('T');
			return dateArray?.length ? dateArray[0] : 'N/A';
		}
		return date;
	};

	/**
	 * @description function to render the status label string based on status index
	 * @memberof PreOpPlanManualUpload
	 */
	getStatusLabel = (planStatus) => {
		switch (Number(planStatus)) {
			case 9:
				return 'Operation Complete';
			case 1:
				return 'Plan Available';
			case 2:
				return 'Plan Exported';
			default:
				return 'N/A';
		}
	};

	/**
	 * @description function to clear the file input value
	 * @memberof PreOpPlanManualUpload
	 */
	resetInput = (event) => {
		event.target.value = null;
		this.resetForm();
	};

	/**
	 * @description function to handle confirmation check box
	 * @memberof PreOpPlanManualUpload
	 */
	handleCheckboxChange = () => {
		this.setState((prevState) => ({
			isChecked: !prevState.isChecked,
			disclaimerCheckBox: !prevState.isChecked
				? ""
				: errorMessages.fileUpload.disclaimerCheckBox,
		}));
	};

  	/**
	 * @description function to handle procedure type change
	 * @memberof PreOpPlanManualUpload
	 */
  	handleProcedureChange = (event) => {
    	this.setState({
      	procedureType: event.target.value,
        errorMessage:""
       });
    };
	/**
	 * @description function to handle upload selected zip file to backend end point 
	 * @memberof PreOpPlanManualUpload
	 */
	validateAndUpload = () => {
		const { isChecked, selectedFile, procedureType } = this.state;
		if (isChecked && selectedFile) {
			this.setState({ isUploading: true, errorMessage:'' });
			const formData = new FormData();
			formData.append("file", selectedFile);
      // Choose the appropriate upload method based on procedure type
      const uploadMethod =
        procedureType === "shoulder"
          ? uploadPreOpPlanShoulderZip
          : uploadPreOpPlanZip;

      const reviewMethod =
        procedureType === "shoulder"
          ? reviewPreOpPlanShoulderDetails
          : reviewPreOpPlanDetails;

      uploadMethod(formData, (err, result) => {
        if (!this.state.showModal) return;

        if (err) {
			this.setState({ errorMessage: err.errorMessage || err.message, isUploading: false });
        } else {
          reviewMethod(result.data, (err, resp) => {
						if (!this.state.showModal) return

						if (err) {
							this.setState({
								errorMessage: err.errorMessage || err.message,
								isUploading: false,
							});
						} else {
							const { preopCaseData, duplciatePreopPlan } = resp.data;
							this.setState({
								preopCaseData,
								duplciatePreopPlan,
								fileDataUploadConfirmation: true,
								isUploading: false
							});
						}
					});
				}
			});
		}
	};

	/**
	 * @description function to format full name
	 * @memberof PreOpPlanManualUpload
	 */
	formatFullName(lastName, firstName, middleName) {
		let firstN = firstName?.trim() || 'Unknown';
		let middleN = middleName?.trim() || '';
		let lastN = lastName?.trim() || 'Unknown';
		return `${lastN}, ${firstN}${middleN && ','} ${middleN}`;
	}

	/**
	 * @description function to confirming the uploaded zip file
	 * @memberof PreOpPlanManualUpload
	 */
	confirmUploadedData = () => {
		this.setState({ isUploading: true });

    const { procedureType } = this.state;

    // Choose the appropriate confirm method based on procedure type
    const confirmMethod =
      procedureType === "shoulder"
        ? confirmUploadShoulderPreOpPlanZip
        : confirmUploadPreOpPlanZip;

    confirmMethod(
		{ ...this.state.preopCaseData },
			(err, result) => {
				if (!this.state.showModal) return

				if (err) {
					this.setState({ errorMessage: err.errorMessage, isUploading: false });
				} else {
					this.setState({
						isUploading: false,
						uploadSuccess: true,
					});
					// after confirmation success it will wait for 5 seconds to call the preop list data
					setTimeout(() => {
						this.props.refreshData();
						this.closeModal();
					}, 5000);
				}
			}
		);
	};

  // Function to render the file upload form
  renderFileUploadForm() {
      const { fileName, errorMessage, isChecked, disclaimerCheckBox, selectedFile, isUploading, procedureType } = this.state;
    return (
      <>
	  	{isUploading ? <div className="loading-overlay manual-pre-op-upload-loader"><span>{actions.upload}</span></div> : ''}
        <div className="row">
          <div className="col">
            <div className="body body-section">
              <div className="upload-wrap">
                <div className="form-group w-100">
                  <label>Procedure<span className="text-danger">*</span>:</label>
                  <div className="radio-group m-0">
                    <div className="form-check">
                      <input
						type="radio"
						className="form-check-input"
						value="knee"
						id="upload-knee"
						name="procedure"
						onChange={this.handleProcedureChange}
						checked={procedureType === "knee"}
                      />
                      <label htmlFor="upload-knee" className="form-check-label">Knee</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        value="shoulder"
                        id="upload-shoulder"
                        name="procedure"
                        onChange={this.handleProcedureChange}
                        checked={procedureType === "shoulder"}
                      />
                      <label htmlFor="upload-shoulder" className="form-check-label">Shoulder</label>
                    </div>
                  </div>
                </div>
				
                <div className="form-group w-100">
                  <label htmlFor="inputFile">
                    Select File<span className="text-danger">*</span>:
                  </label>
                  <div className="input-group gap-2">
                    <input
                      type="file"
                      ref={this.inputOpenFileRef}
                      id="inputFile"
                      data-testid="inputFile"
                      onChange={(e) => this.handleChange(e.target.files)}
                      onClick={this.resetInput}
                      className="form-control file input-file d-none"
                    />
                    <input
                      placeholder="Select File"
                      type="text"
                      value={fileName}
                      readOnly
                      onClick={this.showOpenFileDlg}
                      className="form-control input-file rounded disable-input"
                      size="60"
                    />
                    <div className="input-group-append">
                         <button className="btn btn-secondary" onClick={this.showOpenFileDlg}>Browse</button>
                    </div>
                  </div>
                  {errorMessage && <small className="text-danger">{errorMessage}</small>}
                </div>
              </div>

              <div className="file-upload-from-container mx-1">
                <div className="form-check">
                  <input
                    type="checkbox"
                    id="disclaimerchk"
                    data-testid="disclaimerchk"
                    className="form-check-input"
                    checked={isChecked}
                    onChange={this.handleCheckboxChange}
                  />
				  <label className="form-check-label text-justify" htmlFor="disclaimerchk">
					  {errorMessages.fileUpload.check1}&nbsp;{errorMessages.fileUpload.check2}<span className="text-danger">*</span>
                  </label>
                  {disclaimerCheckBox && (<small className="text-danger">{disclaimerCheckBox}</small>)}
                </div>
              </div>

              <div className="d-flex justify-content-end gap-1 text-center mt-3">
                <button className="btn btn-light" onClick={this.closeModal} disabled={isUploading}>
					Cancel
				</button>
                <button
					className="btn btn-primary"
					id="fileUpload"
					data-testid="fileUpload"
					onClick={this.validateAndUpload}
					disabled={!isChecked || !selectedFile}>
					Upload
				</button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Function to render knee procedure details
  renderKneeDetails() {
    const { preopCaseData, duplciatePreopPlan } = this.state;

    return (
      <div className="row justify-content-center">
        <div className="col-md-12">
          {duplciatePreopPlan && (
            <div className="alert alert-warning p-2" role="alert">
              <strong>Note:</strong>&nbsp;{errorMessages.preopPlans.preopPlanDuplicate}
            </div>
          )}

          <div className="card mb-2">
            <div className="card-header"><strong>Patient & Surgery Details - Knee</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-2">
					<label className="preop-info"><strong>Pre-op Plan ID:</strong></label>
                    <div className="preop-info">{preopCaseData?.caseNumber}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Surgeon Name:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.surgeonName}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Sales Rep:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.salesrepName}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Hospital Association:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.hospitalName}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Implant Type:</strong></label>
                    <div className="preop-info">{preopCaseData?.operativeImplant || 'N/A'}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Status:</strong></label>
                    <div className="preop-info">{this.getStatusLabel(preopCaseData?.planStatus)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Procedure:</strong></label>
                    <div className="preop-info">{preopCaseData?.procedureName}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Op Side & Condyle:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.operativeSide}&nbsp;{preopCaseData?.operativeCondyle}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Patient Name:</strong></label>
                    <div className="preop-info text-capitalize">{this.formatFullName(preopCaseData?.patientLastName, preopCaseData?.patientFirstName, preopCaseData?.patientMiddleName)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Patient DOB:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.patientDOB, true)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Patient Sex:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.gender}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Org Hospital Info:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.hospitalName}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Date of Expiration:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.expirationDate)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Surgery Date:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.surgeryDate, true)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Last Update:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.updatedDate)}</div>
                  </div>
                </div>
              </div>
            </div>
           </div>
        </div>
      </div>
	  )}

  // Function to render shoulder procedure details
  renderShoulderDetails() {
    const { preopCaseData, duplciatePreopPlan } = this.state;

    return (
      <div className="row justify-content-center">
        <div className="col-md-12">
          {duplciatePreopPlan && (
            <div className="alert alert-warning p-2" role="alert">
              <strong>Note:</strong>&nbsp;
              {errorMessages.preopPlans.preopPlanDuplicate}
            </div>
          )}

         <div className="card mb-2">
            <div className="card-header"><strong>Patient & Surgery Details - Shoulder</strong></div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Pre-op Plan ID:</strong></label>
                    <div className="preop-info">{preopCaseData?.caseNumber}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Surgeon Name:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.surgeonName}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Sales Rep:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.salesrepName}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Hospital Association:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.hospitalName}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Implant Type:</strong></label>
                    <div className="preop-info">{preopCaseData?.operativeImplant || 'N/A'}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Status:</strong></label>
                    <div className="preop-info">{this.getStatusLabel(preopCaseData?.planStatus)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Procedure:</strong></label>
                    <div className="preop-info">{preopCaseData?.procedureName}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Operative Side:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.operativeSide}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Patient Name:</strong></label>
                    <div className="preop-info text-capitalize">{this.formatFullName(preopCaseData?.patientLastName, preopCaseData?.patientFirstName, preopCaseData?.patientMiddleName)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Patient DOB:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.patientDOB, true)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Patient Sex:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.gender}</div>
                  </div>
				  <div className="mb-2">
                    <label className="preop-info"><strong>Org Hospital Info:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.hospitalName}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="mb-2">
                    <label className="preop-info"><strong>Date of Expiration:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.expirationDate)}</div>
                  </div>
                  <div className="mb-2">
                    <label className="preop-info"><strong>Surgery Date:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.surgeryDate, true)}</div>
                  </div>
				  <div className="mb-2">
                    <label className="preop-info"><strong>Last Update:</strong></label>
                    <div className="preop-info">{this.renderDate(preopCaseData?.updatedDate)}</div>
                  </div>
				  <div className="mb-2">
                    <label className="preop-info"><strong>Procedure Type:</strong></label>
                    <div className="preop-info text-capitalize">{preopCaseData?.requestedProcedure}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

  // Function to render the file upload confirmation
  renderFileDataUploadConfirmation() {
    const {
      preopCaseData,
      uploadSuccess,
      errorMessage,
      isUploading,
      procedureType,
    } = this.state;

    return (
      <div className="row">
        <div className="col">
          {uploadSuccess || errorMessage ? (
            <div className="row">
              <div className="col-md-12">
                {uploadSuccess && (
                  <p className="text-success m-0 fs-9-rem">
                    {errorMessages.preopPlans.manualUploadSuccess}
                  </p>
                )}
                {errorMessage && (
                  <p className="text-danger m-0 fs-9-rem">{errorMessage}</p>
                )}
              </div>
            </div>
          ) : (
            ""
          )}
          {preopCaseData && Object.keys(preopCaseData).length && (
            procedureType === "shoulder" 
              ? this.renderShoulderDetails() 
              : this.renderKneeDetails()
          )}
          <div className="d-flex justify-content-end gap-1 text-center mt-2">
						{!uploadSuccess && (<button className="btn btn-light" type="button" onClick={this.closeModalWithLog}>Cancel</button>)}
						{uploadSuccess && (<button className="btn btn-primary" type="button" onClick={this.closeModal}>Ok</button>)}
						{!uploadSuccess && (
							<button className="btn btn-primary" type="button" data-testid="confirm-btn" onClick={!uploadSuccess && this.confirmUploadedData} disabled={isUploading}>
								{isUploading ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Confirm'}
							</button>
						)}
					</div>
				</div>
			</div >
		);
	}

	render() {
		const { showModal, fileDataUploadConfirmation } = this.state;

		return (
			<div>
				<button data-testid="preopManualUpload"
					onClick={this.openModal}
					className='preop-upload-btn mt-1'
					style={{
						backgroundImage: `url(${getImgSrc('file-upload-white.png')})`,
					}}
				>
				</button>

				<CustomModal
					show={showModal}
					dialogClassName="overlapping-points-modal"
					isComponent={true}
					title="PreOp Plan Manual Upload"
					rejectAction={this.closeModal}
					closeAction={this.closeModal}
					hideClose={true}
				>
					{fileDataUploadConfirmation
						? this.renderFileDataUploadConfirmation()
						: this.renderFileUploadForm()}
				</CustomModal>
			</div>
		);
	}
}
